import React, { useEffect, useMemo } from "react";
import { graphql, useFragment } from "react-relay";
import {
  AHStatusCircle,
  Box,
  Space,
  useCircleSize,
  useLayoutContext,
  useTheme,
} from "arkh";
import { useNavigation } from "@react-navigation/native";
import { UserLoggedInNavigatorNavigationProp } from "../../../TopLevelNavigators/UserLoggedIn/UserLoggedInNavigatorParamList";
import { Pressable, View } from "react-native";

import Animated, {
  Easing,
  interpolateColor,
  SharedValue,
  useAnimatedReaction,
  useAnimatedStyle,
  useSharedValue,
  withSequence,
  withSpring,
  withTiming,
} from "react-native-reanimated";
import Color from "color";
import { TeamScheduleRoutineListItemContent } from "../../Team/TeamHomeNavigator/TeamScheduleListItem/TeamScheduleRoutineListItemContent";
import { getControlType } from "../../RoutineAdmin/RoutineEditMain/RoutineEditControlTypeCard/getControlType";
import { TeamScheduleAdminItemComponent_routine$key } from "./__generated__/TeamScheduleAdminItemComponent_routine.graphql";
import { getTeamScheduleListItemHeight } from "../../Team/TeamHomeNavigator/TeamScheduleListItem/getTeamScheduleListItemHeight";
import { ListItemRoles } from "widgets/src/TodoListItem/TodoListItemFooter/ListItemRoles";

export function TeamScheduleAdminItemComponent(props: {
  spaceTop?: Space;
  isFirst: boolean;
  isLast: boolean;
  routine: TeamScheduleAdminItemComponent_routine$key;

  activeOccasionsIndicator: React.ReactElement;
  timeIndicator: React.ReactElement | null;
  newlyCreatedId: SharedValue<string | null>;
  itemType: "timeOfDay" | "WEEKLY" | "MONTHLY";
}) {
  const routine = useFragment(
    graphql`
      fragment TeamScheduleAdminItemComponent_routine on Routine {
        id
        title
        isOrg
        activeVersion {
          tasks {
            id
            title
            ... on TaskCheck {
              sentiments
            }
            __typename
          }
        }
        assignedRoles {
          role {
            id
            ...ListItemRoles_roles
          }
        }

        ...TeamScheduleRoutineListItemContent_Routine
      }
    `,
    props.routine
  );

  const roles = useMemo(
    () =>
      routine.assignedRoles
        .filter((r) => r != null && r.role != null)
        .map((r) => r.role),
    [routine.assignedRoles]
  );

  const routineType = useMemo(() => {
    return getControlType(
      routine.activeVersion.tasks.map((t) => ({
        type: t.__typename,
        title: t.title,
        sentiments: t.sentiments,
      }))
    );
  }, [routine.activeVersion.tasks]);

  const animation = useSharedValue(0);

  useEffect(() => {
    animation.value = 0;
  }, [animation, routine.id]);

  const newlyCreatedId = props.newlyCreatedId;

  useAnimatedReaction(
    () => newlyCreatedId.value === routine.id,
    (isSelected, prev) => {
      if (isSelected && !prev) {
        animation.value = withSequence(
          withSpring(1),
          withTiming(0, { duration: 1500, easing: Easing.sin }, () => {
            newlyCreatedId.value = null;
          })
        );
      }
    }
  );

  const theme = useTheme();

  const color = useMemo(
    () => Color(theme.primary).mix(Color(theme.surface), 0.75).toString(),
    [theme.primary, theme.surface]
  );

  const style = useAnimatedStyle(() => ({
    backgroundColor: interpolateColor(
      animation.value,
      [0, 1],
      [theme.surface, color]
    ),
  }));

  const layout = useLayoutContext();

  const nav =
    useNavigation<UserLoggedInNavigatorNavigationProp<"TeamScheduleAdmin">>();

  return (
    <Box
      style={{
        flexDirection: "row",
        zIndex: 1,
        alignItems: "center",
        height: getTeamScheduleListItemHeight(
          props.itemType === "timeOfDay" ? "DAILY" : props.itemType,
          false
        ),
      }}
    >
      <Pressable
        disabled={routine.isOrg}
        onPress={() => {
          nav.navigate("RoutineEdit", { id: routine.id });
        }}
        style={(p) => ({
          height: "100%",
          flex: 1,
          opacity: routine.isOrg ? 0.5 : p.pressed ? 0.5 : 1,
        })}
      >
        <Animated.View
          style={[
            style,
            {
              flexDirection: "row",
              width: "100%",
              flex: 1,
              alignItems: "center",
              paddingRight: layout.space.medium,
              borderColor: theme["border-primary"],
              borderWidth: 1,
              borderTopWidth:
                props.itemType === "timeOfDay" ? 0 : props.isFirst ? 1 : 0,

              borderBottomLeftRadius: props.isLast ? layout.radius.large : 0,
              borderBottomRightRadius: props.isLast ? layout.radius.large : 0,
              borderTopLeftRadius:
                props.itemType !== "timeOfDay" && props.isFirst
                  ? layout.radius.large
                  : 0,
              borderTopRightRadius:
                props.itemType !== "timeOfDay" && props.isFirst
                  ? layout.radius.large
                  : 0,
            },
          ]}
        >
          {props.timeIndicator}
          <View
            style={{
              flex: 1,
              justifyContent: "center",
            }}
          >
            <TeamScheduleRoutineListItemContent
              routine={routine}
              top={props.activeOccasionsIndicator}
              occasion={null}
            />
            {roles.length > 0 ? (
              <Box
                spaceTop="small"
                style={{ flexDirection: "row", alignItems: "center" }}
              >
                <ListItemRoles
                  roles={roles}
                  maxShowingRoles={3}
                  highlightViewerRoles={false}
                />
              </Box>
            ) : null}
          </View>
          <RoutineTypeIndicator type={routineType} />
        </Animated.View>
      </Pressable>
    </Box>
  );
}

function RoutineTypeIndicator(props: { type: "checklist" | "check" | "none" }) {
  const theme = useTheme();
  const size = useCircleSize("medium");
  switch (props.type) {
    case "checklist":
      return (
        <Box style={{ opacity: 0.5 }}>
          <AHStatusCircle
            testID="RoutineTypeIndicator_checklist"
            filledColor={theme.washedText}
            shouldInsetFill={false}
            progress={0}
            icon="arrow-right"
            iconColor={theme.washedText}
            trackIsFilled
            trackFillColor={theme.washedText}
            isFilled={false}
            size={size}
            trackBackgroundColor={theme.washedText}
          />
        </Box>
      );
    case "check":
      return (
        <Box style={{ opacity: 0.5 }}>
          <AHStatusCircle
            testID="RoutineTypeIndicator_check"
            filledColor={theme.washedText}
            shouldInsetFill={false}
            progress={0}
            trackIsFilled
            trackFillColor={theme.washedText}
            isFilled={false}
            size={size}
            trackBackgroundColor={theme.washedText}
          />
        </Box>
      );
    default:
      return null;
  }
}
